import {
  EMPTY_VALUE,
  FieldSchema,
  FieldValue,
  FieldValues,
  isEmpty,
} from "../Schema";
import {
  DataCardinalities,
  DataEnumSchema,
  DataTypes,
  DataVariants,
  StandardEnumOption,
  isIsoDate,
} from "../data/DataManager";
import { lookupLabel, normalizeOptions } from "../display/format";
import { prepareDynamicPropertyValue } from "../Schema";

/** Renders a field value as the text of a single CSV cell. */
export type CsvTranslator = (
  value: FieldValue,
  values?: FieldValues
) => Promise<string> | string;

/** Parses the text of a single CSV cell back into a field value. */
export type CsvParser = (
  cell: string,
  values?: FieldValues
) => Promise<FieldValue> | FieldValue;

/** An empty field is written as an empty cell, never as "null" or "-". */
export const CSV_EMPTY_CELL = "";

/** Cell texts that mean "no value" on the way in. Mirrors the placeholders the exports emit. */
const EMPTY_CELLS = ["", "-", "null", "undefined"];

/** Separator for `DataCardinalities.MULTIPLE` values inside one cell. */
const MULTIPLE_SEPARATOR = ",";

const TRUTHY_CELLS = ["yes", "true", "1"];
const FALSY_CELLS = ["no", "false", "0"];

/** A string of digits with a meaningful leading zero, which Excel would otherwise eat. */
const LEADING_ZERO = /^0\d+$/;

// Excel writes dates back unpadded, so the month and day parts accept one or two digits.
// The year is always required in full: a two-digit year cannot be told apart from a day,
// so "09-03-26" is rejected rather than guessed at.
const ISO_DATE_ONLY = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const ISO_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T/;
const DMY_DATE_DASH = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
const DMY_DATE_SLASH = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

const pad = (part: string | number): string => String(part).padStart(2, "0");

/** True when the cell carries no value. */
export const isEmptyCell = (cell: string): boolean =>
  EMPTY_CELLS.includes(cell?.trim?.() ?? "");

/**
 * Formats a date-ish value as `DD-MM-YYYY`, the format every equipment CSV uses.
 * Accepts the ISO and day-first shapes the stored data mixes.
 *
 * @param value - The value to format.
 * @returns The formatted date, or an empty cell when it is not a date.
 */
export const formatDateCell = (value: FieldValue): string => {
  // Checked before isEmpty, which reports any object with no own enumerable keys as
  // empty — and a Date has none, so it would otherwise always render as a blank cell.
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ?
      CSV_EMPTY_CELL :
      `${pad(value.getUTCDate())}-${pad(value.getUTCMonth() + 1)}-${value.getUTCFullYear()}`;
  }

  if (isEmpty(value) || typeof value !== "string") {
    return CSV_EMPTY_CELL;
  }

  const normalized = value.trim();
  if (isEmptyCell(normalized)) {
    return CSV_EMPTY_CELL;
  }

  const isoDate = normalized.match(ISO_DATE_ONLY) ?? normalized.match(ISO_DATE_TIME);
  if (isoDate) {
    const [, year, month, day] = isoDate;
    return `${pad(day)}-${pad(month)}-${year}`;
  }
  // Already day-first: normalise separator and padding, keep the order.
  const dayFirst = normalized.match(DMY_DATE_DASH) ?? normalized.match(DMY_DATE_SLASH);
  if (dayFirst) {
    const [, day, month, year] = dayFirst;
    return `${pad(day)}-${pad(month)}-${year}`;
  }

  // Deliberately no `new Date(string)` fallback: it resolves in the host timezone and
  // silently shifts the day, and it reads an ambiguous two-digit year as a month.
  return CSV_EMPTY_CELL;
};

/**
 * Parses a `DD-MM-YYYY` cell — or any of the other shapes users paste — into the
 * `YYYY-MM-DD` form the backend stores.
 *
 * @param cell - The cell text to parse.
 * @returns The ISO date, the cell's own text when it is not a date, or `EMPTY_VALUE` when the
 *          cell is empty.
 */
export const parseDateCell = (cell: string): FieldValue => {
  if (isEmptyCell(cell)) {
    return EMPTY_VALUE;
  }
  const normalized = stripProtectiveQuotes(cell);

  // Matching the shape is not enough — the parts have to name a day that exists. `32-13-2026`
  // is a plausible typo and rearranges into `2026-13-32`, which is ISO-shaped, so a shape test
  // alone let the thirteenth month through to the database.
  const asRealDate = (year: string, month: string, day: string): string | undefined => {
    const iso = `${year}-${pad(month)}-${pad(day)}`;
    return isIsoDate(iso) ? iso : undefined;
  };

  const dayFirst = normalized.match(DMY_DATE_DASH) ?? normalized.match(DMY_DATE_SLASH);
  if (dayFirst) {
    const [, day, month, year] = dayFirst;
    return asRealDate(year, month, day) ?? normalized;
  }
  const isoDate = normalized.match(ISO_DATE_ONLY) ?? normalized.match(ISO_DATE_TIME);
  if (isoDate) {
    const [, year, month, day] = isoDate;
    return asRealDate(year, month, day) ?? normalized;
  }

  // Same reasoning as formatDateCell: no `new Date(string)` fallback, which would store a
  // date one day off or with the day and month transposed.
  //
  // The text is kept rather than blanked, exactly as an unmatched enum cell is. Blanking it
  // here makes "no date" and "not a date" indistinguishable by the time anything can report
  // on them, and the field's own validation then has nothing to object to — so a typo in a
  // date column silently cleared the stored value. `findUnparseableValues` reports what
  // survives, and `DataManager` reduces it to `EMPTY_VALUE` before the row is written.
  return normalized;
};

/**
 * Quotes a digit string whose leading zero would otherwise be lost when Excel opens
 * the file. Everything else passes through untouched.
 *
 * @param cell - The cell text to protect.
 * @returns The cell, quoted when it needs protecting.
 */
export const protectLeadingZeros = (cell: string): string =>
  LEADING_ZERO.test(cell) ? `"${cell}"` : cell;

/**
 * Removes the quoting {@link protectLeadingZeros} adds, so a round trip is lossless.
 *
 * @param cell - The cell text to unwrap.
 * @returns The cell without its protective quotes.
 */
export const stripProtectiveQuotes = (cell: string): string => {
  const normalized = cell?.trim?.() ?? "";
  if (normalized.length > 1 && normalized.startsWith("\"") && normalized.endsWith("\"")) {
    const inner = normalized.slice(1, -1);
    if (LEADING_ZERO.test(inner)) {
      return inner;
    }
  }
  return normalized;
};

/**
 * Resolves a field's enum options, which may be declared statically or supplied by a
 * dynamic getter that depends on other field values.
 */
export type OptionsResolver = (
  field: FieldSchema,
  values?: FieldValues
) => Promise<StandardEnumOption<FieldValue>[]>;

/**
 * Builds a memoising options resolver.
 *
 * A dynamic options getter usually goes to the network, and a file's rows overwhelmingly
 * repeat the same dependency values — every row of a single-site export resolves the same
 * building list. Without this, a 900-row export re-fetches each dynamic enum 900 times.
 *
 * The cache is keyed by the field plus the values its getter actually declared a
 * dependency on, so a genuinely different row still resolves its own options. Promises
 * are cached rather than results, so concurrent rows share one in-flight request.
 *
 * @returns A resolver with its own cache. Safe to share across rows; it holds no
 *          per-row state and never mutates its inputs.
 */
export const createOptionsResolver = (): OptionsResolver => {
  const cache = new Map<string, Promise<StandardEnumOption<FieldValue>[]>>();

  return (field, values = {}) => {
    const prepared = prepareDynamicPropertyValue(
      (field.data as DataEnumSchema)?.options ?? []
    );
    const dependencies = prepared.fields.map((name) => values[name]);
    const key = `${field.name}|${JSON.stringify(dependencies)}`;

    let resolved = cache.get(key);
    if (!resolved) {
      resolved = (async () => normalizeOptions(await prepared.getter(values)))();
      cache.set(key, resolved);
    }
    return resolved;
  };
};

/** Fallback for standalone use: correct, but resolves the options every call. */
const resolveOptionsUncached: OptionsResolver = (field, values = {}) =>
  createOptionsResolver()(field, values);

/**
 * Aligns a stored value with the type its options are declared in.
 *
 * A boolean is not stored as a boolean everywhere: a search index keeps it as 0/1, and a
 * CSV round trip may hand back "true". Options for such a field are declared with real
 * boolean values, so a strict lookup would miss and the raw 0 would be written to the file
 * instead of "No". Only boolean-valued option sets are coerced; anything else is left
 * exactly as it is, so a string enum that happens to contain "0" keeps that value.
 *
 * @param value - The stored value.
 * @param options - The field's normalised options.
 * @returns The value, coerced when the options are boolean-valued and it is not.
 */
export const coerceToOptionType = (
  value: FieldValue,
  options: StandardEnumOption<FieldValue>[]
): FieldValue => {
  const isBooleanEnum = options.length > 0 &&
    options.every((option) => typeof option.value === "boolean");
  if (!isBooleanEnum || typeof value === "boolean") {
    return value;
  }
  const normalized = String(value).trim().toLowerCase();
  if (TRUTHY_CELLS.includes(normalized)) {
    return true;
  }
  if (FALSY_CELLS.includes(normalized)) {
    return false;
  }
  return value;
};

/**
 * Builds the translator for one value of a field — the singular case, before
 * cardinality is taken into account.
 *
 * @param field - The field to build the translator for.
 * @returns The translator for a single value.
 */
const prepareSingularTranslator = (
  field: FieldSchema,
  resolveOptions: OptionsResolver
): CsvTranslator => {
  if (field.data?.variant === DataVariants.ENUM) {
    return async (value, values) => {
      if (isEmpty(value)) {
        return CSV_EMPTY_CELL;
      }
      const options = await resolveOptions(field, values);
      // Falling back to the raw value keeps data the options no longer cover visible
      // in the export rather than silently blanking it.
      return lookupLabel(coerceToOptionType(value, options), options) ?? String(value);
    };
  }

  switch (field.data?.type) {
    case DataTypes.BOOLEAN:
      // Tolerant of the 0/1 a search index holds, not just real booleans.
      return (value) => {
        if (isEmpty(value)) {
          return CSV_EMPTY_CELL;
        }
        const coerced = coerceToOptionType(value, [
          { value: true, label: "Yes" },
          { value: false, label: "No" },
        ]);
        return coerced ? "Yes" : "No";
      };
    case DataTypes.DATE:
      return (value) => formatDateCell(value);
    case DataTypes.NUMBER:
      return (value) => (isEmpty(value) ? CSV_EMPTY_CELL : String(value));
    case DataTypes.OBJECT:
      return (value) => {
        if (isEmpty(value) || typeof value !== "object") {
          return CSV_EMPTY_CELL;
        }
        // `{ key, value }` master-data pairs export as the human-facing half; any other
        // object falls back to joining its primitive members.
        const record = value as Record<string, FieldValue>;
        if ("value" in record) {
          return isEmpty(record.value) ? CSV_EMPTY_CELL : String(record.value);
        }
        return Object.values(record)
          .filter((inner) => ["string", "number", "boolean"].includes(typeof inner))
          .map(String)
          .join(" ");
      };
    default:
      return (value) => (isEmpty(value) ? CSV_EMPTY_CELL : String(value));
  }
};

/**
 * Builds the parser for one value of a field — the singular case, before cardinality
 * is taken into account.
 *
 * @param field - The field to build the parser for.
 * @returns The parser for a single cell.
 */
const prepareSingularParser = (
  field: FieldSchema,
  resolveOptions: OptionsResolver
): CsvParser => {
  if (field.data?.variant === DataVariants.ENUM) {
    return async (cell, values) => {
      if (isEmptyCell(cell)) {
        return EMPTY_VALUE;
      }
      const normalized = stripProtectiveQuotes(cell);
      const options = await resolveOptions(field, values);
      // A cell holds the label, always — the same text the translator writes and the same
      // text the form shows. Matching the stored value as well used to be the fallback, and
      // it made a column mean two things at once: `checkInGracePeriod` accepted both "15 min"
      // and "15", `isHazardous` both "Yes" and "TRUE". One spelling per column is what a
      // user can be told, and what a template can demonstrate.
      const byLabel = options.find(
        (option) => option.label.toLowerCase() === normalized.toLowerCase()
      );
      // No match: keep the raw text rather than blanking it. DataManager will reduce it
      // to EMPTY_VALUE later, so `validateCsvRow` checks enum membership itself and
      // reports the offending cell instead of letting the value vanish silently.
      //
      // A field that has to accept some further spelling says so itself, with
      // `dataTransform.fromCsv` — `linkedInstance` resolves the name the index stores that
      // way, because its options are labelled with the display name instead.
      return byLabel ? byLabel.value : normalized;
    };
  }

  switch (field.data?.type) {
    case DataTypes.BOOLEAN:
      return (cell) => {
        const normalized = stripProtectiveQuotes(cell).toLowerCase();
        if (TRUTHY_CELLS.includes(normalized)) {
          return true;
        }
        if (FALSY_CELLS.includes(normalized)) {
          return false;
        }
        return EMPTY_VALUE;
      };
    case DataTypes.DATE:
      return (cell) => parseDateCell(cell);
    case DataTypes.NUMBER:
      return (cell) => {
        if (isEmptyCell(cell)) {
          return EMPTY_VALUE;
        }
        const text = stripProtectiveQuotes(cell);
        const parsed = Number(text);
        // Text that is not a number is kept, not blanked. Returning EMPTY_VALUE here
        // would erase the cell before the field's own validation could report it — the
        // same silent loss the enum parser avoids for an unrecognised option.
        return Number.isNaN(parsed) ? text : parsed;
      };
    case DataTypes.OBJECT:
      return (cell) => {
        if (isEmptyCell(cell)) {
          return EMPTY_VALUE;
        }
        const normalized = stripProtectiveQuotes(cell);
        return { key: normalized, value: normalized };
      };
    default:
      return (cell) =>
        isEmptyCell(cell) ? EMPTY_VALUE : stripProtectiveQuotes(cell);
  }
};

/**
 * Resolves the translator for a field: the one declared on its `csv` aspect, or the
 * default derived from `data.variant`, `data.type` and `data.cardinality`.
 *
 * @param field - The field to resolve the translator for.
 * @param resolveOptions - Options resolver; pass a shared memoising one when formatting
 *                         many rows, so a dynamic options getter runs once per distinct
 *                         set of dependency values instead of once per row.
 * @returns The translator.
 */
export const prepareTranslator = (
  field: FieldSchema,
  resolveOptions: OptionsResolver = resolveOptionsUncached
): CsvTranslator => {
  if (field.csv?.translator) {
    return field.csv.translator as CsvTranslator;
  }

  const translateOne = prepareSingularTranslator(field, resolveOptions);
  if (field.data?.cardinality !== DataCardinalities.MULTIPLE) {
    return translateOne;
  }

  return async (value, values) => {
    if (!Array.isArray(value) || !value.length) {
      return CSV_EMPTY_CELL;
    }
    const cells: string[] = [];
    for (const item of value) {
      cells.push(await translateOne(item, values));
    }
    return cells.filter((cell) => cell !== CSV_EMPTY_CELL).join(MULTIPLE_SEPARATOR);
  };
};

/**
 * Resolves the parser for a field: the one declared on its `csv` aspect, or the default
 * derived from `data.variant`, `data.type` and `data.cardinality`.
 *
 * @param field - The field to resolve the parser for.
 * @param resolveOptions - Options resolver; see {@link prepareTranslator}.
 * @returns The parser.
 */
export const prepareParser = (
  field: FieldSchema,
  resolveOptions: OptionsResolver = resolveOptionsUncached
): CsvParser => {
  const parseOne = prepareSingularParser(field, resolveOptions);
  if (field.data?.cardinality !== DataCardinalities.MULTIPLE) {
    return parseOne;
  }

  return async (cell, values) => {
    if (isEmptyCell(cell)) {
      return [];
    }
    const parsed: FieldValue[] = [];
    for (const item of stripProtectiveQuotes(cell).split(MULTIPLE_SEPARATOR)) {
      const trimmed = item.trim();
      if (!isEmptyCell(trimmed)) {
        parsed.push(await parseOne(trimmed, values));
      }
    }
    return parsed;
  };
};
